import { useMemo, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  AlertTriangle,
  ArrowRight,
  BadgeCheck,
  BellRing,
  Briefcase,
  CheckCircle2,
  CircleHelp,
  ExternalLink,
  Layers3,
  LifeBuoy,
} from "lucide-react";
import { Helmet } from "@/components/Helmet";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  CampaignFaq,
  CtaSection,
  IconCardGrid,
  SectionShell,
  StepList,
  TrialExplainerStrip,
} from "@/components/campaign/CampaignSections";
import {
  CAMPAIGN_SIGNUP_HREF,
  CAMPAIGN_SUPPORT_HREF,
  CampaignLayout,
  CampaignSupportLink,
} from "@/components/campaign/CampaignLayout";
import {
  captureCampaignEvent,
  useCampaignPageView,
  useCampaignSectionView,
} from "@/lib/campaignAnalytics";

const CANONICAL_URL = "https://shadowscan.com.au/linkedin";
const HELP_URL = "/help/add-domains";
const PRIMARY_CTA_LABEL = "Get started with ShadowScan";

const LinkedInLanding = () => {
  const location = useLocation();
  const nextStepsRef = useRef<HTMLDivElement | null>(null);

  useCampaignPageView("linkedin_landing_viewed", {
    page: "linkedin_landing",
    path: location.pathname,
  });
  useCampaignSectionView(nextStepsRef, "linkedin_next_steps_viewed", {
    page: "linkedin_landing",
    path: location.pathname,
    location: "next_steps_section",
  });

  const handleCtaClick = (ctaLocation: string, target: string, isPrimary = false) => {
    captureCampaignEvent("linkedin_cta_clicked", {
      page: "linkedin_landing",
      location: ctaLocation,
      target,
    });

    if (isPrimary) {
      captureCampaignEvent("linkedin_evaluation_started", {
        page: "linkedin_landing",
        location: ctaLocation,
        target,
      });
    }
  };

  const handleSupportClick = (locationLabel: string) => {
    captureCampaignEvent("linkedin_support_clicked", {
      page: "linkedin_landing",
      location: locationLabel,
      target: "support_email",
    });
  };

  const faqItems = useMemo(
    () => [
      {
        question: "What happens when I start?",
        answer: (
          <p>
            You create your portal account first, tell ShadowScan whether you are securing
            your own sites or managing multiple sites, and continue into the right
            onboarding path. If portfolio pricing may be relevant, you can apply during
            signup and keep moving on the standard self-serve path while review is pending.
          </p>
        ),
      },
      {
        question: "How is pricing handled?",
        answer: (
          <p>
            Pricing details are available in-product after login. If you manage multiple
            sites, you can apply for a reviewed account during signup. Standard access
            remains available while any review is in progress.
          </p>
        ),
      },
      {
        question: "Do I need a credit card to create an account?",
        answer: (
          <p>
            You can create your account through the self-serve portal first. If you
            continue on the standard path, billing is handled when monitored domains are
            activated.
          </p>
        ),
      },
      {
        question: "When does billing begin?",
        answer: (
          <p>
            On the standard self-serve path, billing begins when domains are activated and
            monitoring starts. If ShadowScan approves a different arrangement for your
            account, that approved pricing is used instead.
          </p>
        ),
      },
      {
        question: "What happens if onboarding fails?",
        answer: (
          <p>
            You can use the onboarding help page for the most common setup issues, including
            domain mismatch, custom <code>wp-admin</code> paths, expired pairing codes, and
            host restrictions. If you still get stuck, email{" "}
            <a
              href={CAMPAIGN_SUPPORT_HREF}
              onClick={() => handleSupportClick("faq")}
              className="text-primary underline underline-offset-4"
            >
              support@shadowscan.com.au
            </a>{" "}
            and include the domain name plus a short note on where the setup stopped.
          </p>
        ),
      },
      {
        question: "Is this for agencies and developers managing multiple sites?",
        answer: (
          <p>
            Yes. ShadowScan is built for agencies, freelancers, developers, and technical
            teams managing multiple WordPress sites, while still leaving a clear standard
            path for individual technical site owners.
          </p>
        ),
      },
    ],
    [],
  );

  const stickyCta = (
    <div className="w-full space-y-2">
      <div className="flex items-center gap-3">
        <Button
          asChild
          className="flex-1 bg-accent text-accent-foreground hover:bg-accent/90"
        >
          <Link
            to={CAMPAIGN_SIGNUP_HREF}
            onClick={() => handleCtaClick("mobile_sticky", CAMPAIGN_SIGNUP_HREF, true)}
          >
            {PRIMARY_CTA_LABEL}
            <ArrowRight className="h-4 w-4" />
          </Link>
        </Button>
        <Button asChild variant="outline" className="min-h-11 shrink-0">
          <Link
            to={HELP_URL}
            onClick={() => handleCtaClick("mobile_sticky", HELP_URL)}
          >
            Onboarding help
          </Link>
        </Button>
      </div>
      <p className="text-xs leading-relaxed text-muted-foreground">
        Create your account first. If you manage multiple sites, you can apply during
        signup and continue on the standard path in the meantime. Billing starts when
        domains are activated, unless a different arrangement has been agreed with
        ShadowScan.
      </p>
    </div>
  );

  return (
    <CampaignLayout
      helpHref={HELP_URL}
      currentPath="landing"
      stickyCta={stickyCta}
      headerCtaLabel="Get started"
      headerCtaHref={CAMPAIGN_SIGNUP_HREF}
    >
      <Helmet>
        <title>ShadowScan for Agencies and Multi-Site WordPress Teams</title>
        <meta
          name="description"
          content="WordPress security for agencies, freelancers, and teams managing multiple WordPress sites, with free Guard, Basic Hosting, and monitored plan options depending on the service level selected."
        />
        <meta property="og:title" content="ShadowScan for Agencies and Multi-Site WordPress Teams" />
        <meta
          property="og:description"
          content="WordPress security for agencies, freelancers, and teams managing multiple WordPress sites, with free Guard, Basic Hosting, and monitored plan options depending on the service level selected."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={CANONICAL_URL} />
        <meta property="og:image" content="https://shadowscan.com.au/og-image.png" />
        <meta
          name="twitter:title"
          content="ShadowScan for Agencies and Multi-Site WordPress Teams"
        />
        <meta
          name="twitter:description"
          content="WordPress security for agencies and multi-site teams, with free Guard, Basic Hosting, and monitored plan options depending on the selected service level."
        />
        <meta name="twitter:image" content="https://shadowscan.com.au/twitter-image.png" />
        <meta
          name="linkedin:description"
          content="WordPress security for agencies, developers, and site owners managing multiple WordPress sites, with free Guard, Basic Hosting, and monitored plan options."
        />
      </Helmet>

      <section className="relative overflow-hidden py-16 md:py-24">
        <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,rgba(25,212,230,0.12)_0%,rgba(25,212,230,0.07)_42%,rgba(14,17,21,0)_100%)]" />
        <div className="container px-4">
          <div className="grid gap-10 lg:grid-cols-[minmax(0,1.05fr),minmax(360px,0.95fr)] lg:items-start lg:gap-12">
            <div className="relative isolate space-y-8">
              <div className="relative space-y-8">
                <div className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-4 py-2 font-mono text-xs uppercase tracking-[0.22em] text-primary">
                  <span aria-hidden="true" className="h-2 w-2 rounded-full bg-[#22C373]" />
                  Built for multi-site WordPress operators
                </div>

                <div className="space-y-6">
                  <h1 className="max-w-4xl text-4xl font-bold leading-[0.98] md:text-5xl lg:max-w-3xl lg:text-[4.3rem]">
                    Security plans for your WordPress portfolio
                  </h1>
                  <p className="max-w-3xl text-lg leading-relaxed text-muted-foreground md:text-xl">
                    ShadowScan gives agencies, freelancers, and technical teams a calmer way
                    to protect multiple WordPress sites. Create your account first, then
                    choose the service level that fits your portfolio, from free Guard and
                    Basic Hosting through monitored plans with human oversight where included.
                  </p>
                </div>

                <div className="space-y-4">
                  <div className="flex flex-col gap-3 sm:flex-row">
                    <Button
                      asChild
                      size="lg"
                      className="bg-accent text-accent-foreground hover:bg-accent/90"
                    >
                      <Link
                        to={CAMPAIGN_SIGNUP_HREF}
                        onClick={() => handleCtaClick("hero_primary", CAMPAIGN_SIGNUP_HREF, true)}
                      >
                        {PRIMARY_CTA_LABEL}
                        <ArrowRight className="h-4 w-4" />
                      </Link>
                    </Button>
                    <Button asChild size="lg" variant="ghost">
                      <a
                        href="#how-it-works"
                        onClick={() => handleCtaClick("hero_secondary", "#how-it-works")}
                      >
                        See how it works
                      </a>
                    </Button>
                  </div>

                  <p className="max-w-3xl text-sm leading-relaxed text-muted-foreground">
                    Create your account first. If a reviewed account may be relevant, you
                    can apply during signup. Your first domains include a 7-day free trial - no charge until the trial ends.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-5 lg:pt-12">
              <Card className="border-border/80 bg-card/80">
                <CardContent className="space-y-4 p-6">
                  <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-accent">
                    How it starts
                  </p>
                  <ul className="space-y-3 text-sm text-muted-foreground">
                    {[
                      "Create your portal account.",
                      "Add the first WordPress sites you want to secure.",
                      "Apply for reviewed pricing during signup if it is relevant to your account.",
                    ].map((item) => (
                      <li key={item} className="flex items-start gap-3">
                        <CheckCircle2 className="mt-0.5 h-4 w-4 flex-none text-[#22C373]" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="text-sm leading-relaxed text-muted-foreground">
                    Start with a small rollout so you can confirm onboarding, monitoring,
                    and support before expanding further.
                  </p>
                </CardContent>
              </Card>

              <div className="flex flex-wrap gap-3">
                {[
                  "Apply for a reviewed account during signup",
                  "Works alongside existing WAF or edge controls",
                  "Human help if onboarding stalls",
                ].map((item) => (
                  <span
                    key={item}
                    className="rounded-full border border-border/80 bg-card/80 px-4 py-2 text-sm text-muted-foreground"
                  >
                    {item}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <SectionShell
        eyebrow="Why this fits"
        title="A focused path for people managing more than one WordPress site"
        description="If you manage more than one WordPress site, ShadowScan gives you a clearer onboarding path, support model, and day-to-day view of how the service fits the way you work."
      >
        <IconCardGrid
          columns="four"
          items={[
            {
              icon: Layers3,
              title: "Too many dashboards",
              description:
                "Multiple sites often means fragmented visibility and extra time spent confirming what actually matters.",
            },
            {
              icon: BellRing,
              title: "Noisy alerting",
              description:
                "Operators need signal quality and context, not constant interruption with weak prioritization.",
            },
            {
              icon: AlertTriangle,
              title: "Unclear incident ownership",
              description:
                "When something goes wrong, agencies and freelancers still need a documented response path.",
            },
            {
              icon: Briefcase,
              title: "Client risk without a security team",
              description:
                "ShadowScan gives teams clearer visibility, better alert context, and a more manageable response path without assuming every business has in-house security coverage.",
            },
          ]}
        />
      </SectionShell>

      <SectionShell
        id="how-it-works"
        eyebrow="How it works"
        title="Start with a small rollout and see how it works in practice"
        description="Use a small rollout to test onboarding and day-to-day fit with sites you already manage."
      >
        <div ref={nextStepsRef}>
          <StepList
            items={[
              {
                step: "Step 1",
                title: "Create your account",
                description:
                  "Start in the signup flow and let ShadowScan know whether you are securing your own sites or managing sites on behalf of others.",
              },
              {
                step: "Step 2",
                title: "Apply for portfolio pricing if relevant",
                description:
                  "If you manage client or multiple sites and want a reviewed account, submit the short application during signup. Standard access remains available while review is in progress.",
              },
              {
                step: "Step 3",
                title: "Add the first sites you want to secure",
                description:
                  "Complete connector pairing and see how the service fits the way you manage sites.",
              },
            ]}
          />
        </div>
      </SectionShell>

      <SectionShell
        eyebrow="Portfolio pricing"
        title="Suitable multi-site accounts can apply during signup"
        description="Portfolio pricing is handled through a short application and admin review."
      >
        <TrialExplainerStrip
          title="Apply for a reviewed account during signup without slowing down onboarding."
          body="Agencies, developers, and multi-site teams can apply during signup if a reviewed account may be a better fit."
          note="ShadowScan reviews those applications before any alternative account arrangement is confirmed."
        />
      </SectionShell>

      <SectionShell
        eyebrow="Get started"
        title="Create your account and follow the right path for your portfolio"
      >
        <CtaSection
          eyebrow="Get started"
          title="Get started, choose the right setup, and begin with a small rollout"
          description="Go straight into signup, indicate whether a reviewed account may apply, and start onboarding the first WordPress sites you want to secure."
          primaryLabel={PRIMARY_CTA_LABEL}
          primaryHref={CAMPAIGN_SIGNUP_HREF}
          secondaryLabel="How onboarding works"
          secondaryHref={HELP_URL}
          onPrimaryClick={() => handleCtaClick("midpage_cta", CAMPAIGN_SIGNUP_HREF, true)}
          onSecondaryClick={() => handleCtaClick("midpage_cta", HELP_URL)}
        />
      </SectionShell>

      <SectionShell
        eyebrow="Need help?"
        title="If domain onboarding stalls, there is a clear next step"
        description="Use the onboarding guide first, then escalate with enough context for a useful reply."
      >
        <div className="grid gap-5 lg:grid-cols-[1fr,0.8fr]">
          <Card className="border-border/80 bg-card/80">
            <CardContent className="space-y-4 p-6">
              <div className="flex items-center gap-3">
                <CircleHelp className="h-5 w-5 text-primary" />
                <h3 className="text-xl font-semibold">Need help adding domains?</h3>
              </div>
              <p className="text-sm leading-relaxed text-muted-foreground">
                The help page walks through adding the domain, installing the connector,
                generating a pairing code, handling custom <code>wp-admin</code> paths,
                and resolving the setup blockers that most often slow things down.
              </p>
              <div className="flex flex-col gap-3 sm:flex-row">
                <Button asChild variant="outline">
                  <Link
                    to={HELP_URL}
                    onClick={() => handleCtaClick("help_block", HELP_URL)}
                  >
                    Open onboarding help
                  </Link>
                </Button>
                <a
                  href={CAMPAIGN_SUPPORT_HREF}
                  onClick={() => handleSupportClick("help_block")}
                  className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:text-primary/80"
                >
                  Contact support
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
              </div>
            </CardContent>
          </Card>

          <Card className="border-border/80 bg-card/80">
            <CardContent className="space-y-4 p-6">
              <div className="flex items-center gap-3">
                <LifeBuoy className="h-5 w-5 text-accent" />
                <h3 className="text-xl font-semibold">What to include if you email support</h3>
              </div>
              <ul className="space-y-3 text-sm text-muted-foreground">
                {[
                  "The domain name you were onboarding",
                  "Whether the issue happened in the portal, plugin install, or pairing step",
                  "If the site uses a custom wp-admin path or host restrictions",
                  "Any error text or screenshot that helps narrow the failure",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <BadgeCheck className="mt-0.5 h-4 w-4 flex-none text-[#22C373]" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <CampaignSupportLink className="pt-1" />
            </CardContent>
          </Card>
        </div>
      </SectionShell>

      <SectionShell
        eyebrow="FAQ"
        title="Questions people usually ask before getting started"
        description="Keep the answers clear, honest, and easy to act on."
      >
        <CampaignFaq items={faqItems} />
      </SectionShell>

      <SectionShell
        eyebrow="Get started"
        title="See whether ShadowScan fits the way you manage WordPress portfolios"
      >
        <CtaSection
          eyebrow="Next step"
          title="Get started with ShadowScan and begin with a small rollout"
          description="Move through signup, choose the path that fits your account, and onboard a small number of sites you already manage."
          primaryLabel={PRIMARY_CTA_LABEL}
          primaryHref={CAMPAIGN_SIGNUP_HREF}
          secondaryLabel="See onboarding help"
          secondaryHref={HELP_URL}
          onPrimaryClick={() => handleCtaClick("final_cta", CAMPAIGN_SIGNUP_HREF, true)}
          onSecondaryClick={() => handleCtaClick("final_cta", HELP_URL)}
        />
      </SectionShell>
    </CampaignLayout>
  );
};

export default LinkedInLanding;
